import React, { useRef, useState, useMemo, useEffect } from 'react';
import { useApp } from '@/context/AppContext';
import { FileCategory, ProjectFile, Folder } from '@/types';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { downloadMedia } from '@/lib/media/downloadMedia';
import { format } from 'date-fns';
import { de, he } from 'date-fns/locale';
import { toast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import { Download, FileText, Upload, Image as ImageIcon, Video, ChevronLeft, ChevronRight, Share2, Loader2, Pencil, X, CheckCircle2, Circle, FolderInput, FolderPlus, List, FolderTree as FolderTreeIcon } from 'lucide-react';
import { Document, Page, pdfjs } from 'react-pdf';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import ImageGallery from './ImageGallery';
import { isImageMedia, isVideoMedia, isPdfMedia } from '@/lib/media/mediaTypes';
import { canOpenPdfNatively, openPdfNative } from '@/lib/media/openPdfNative';
import { fileService } from '@/services/fileService';
import { toast as tst } from 'sonner';
import { Capacitor } from '@capacitor/core';
import { shareAttachments, shareAttachmentsWeb, ShareItem } from '@/lib/shareAttachments';
import RenameFileDialog from '@/components/RenameFileDialog';
import ChangeCategoryDialog from '@/components/ChangeCategoryDialog';
import NewFolderDialog from '@/components/NewFolderDialog';
import FolderTree from '@/components/documents/FolderTree';
import { FolderTreeShared } from '@/components/documents/FolderNode';

pdfjs.GlobalWorkerOptions.workerSrc =
  `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;

interface DocumentsTabProps {
  projectId: string;
}

const categoryLabels: Record<FileCategory, string> = {
  plans: 'תוכניות',
  payments: 'תשלומים',
  contracts: 'חוזים',
  photos: 'מדיה'
};

export default function DocumentsTab({ projectId }: DocumentsTabProps) {
  const { getProjectFiles } = useApp();
  const [selectedFile, setSelectedFile] = useState<ProjectFile | null>(null);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [galleryIndex, setGalleryIndex] = useState(0);
  const [isGalleryOpen, setIsGalleryOpen] = useState(false);
  const [filesList, setFilesList] = useState<Object>({
    plans: [],
    payments: [],
    contracts: [],
    photos: []
  });
  const [activeTab, setActiveTab] = useState<FileCategory>('plans');
  const [pdfNumPages, setPdfNumPages] = useState<number>(0);
  const [pdfCurrentPage, setPdfCurrentPage] = useState<number>(1);
  const fileInputRefs = useRef<Record<FileCategory, HTMLInputElement | null>>({
    plans: null,
    payments: null,
    contracts: null,
    photos: null
  });

  // ---- File selection (Share / Rename), mirrors NewDocumentModal ----
  const isAndroid = Capacitor.getPlatform() === 'android';
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [isSharing, setIsSharing] = useState(false);
  const [renameTargetId, setRenameTargetId] = useState<string | null>(null);
  const [isRenaming, setIsRenaming] = useState(false);
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [isChangingCategory, setIsChangingCategory] = useState(false);
  const longPressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const longPressFiredRef = useRef(false);

  // ---- Folder hierarchy (backend-backed; document tabs only) ----
  // View mode is a single shared value → it persists across the 3 doc tabs while mounted.
  const [viewMode, setViewMode] = useState<'flat' | 'folder'>('flat');
  const [folders, setFolders] = useState<Folder[]>([]);
  const [foldersLoading, setFoldersLoading] = useState(false);
  const [isCreatingFolder, setIsCreatingFolder] = useState(false);
  const [expandedFolderIds, setExpandedFolderIds] = useState<Set<string>>(new Set());
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);
  const [newFolderOpen, setNewFolderOpen] = useState(false);

  const toggleFolderExpand = (id: string) => {
    setExpandedFolderIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Apply a folderId to the matching files across every category array (immutable).
  const applyFolderToState = (ids: Set<string>, folderId: string | null) => {
    setFilesList(prev => {
      const f = prev as Record<FileCategory, ProjectFile[]>;
      const next: Record<string, ProjectFile[]> = {};
      (Object.keys(f) as FileCategory[]).forEach(cat => {
        next[cat] = (f[cat] || []).map(file => (ids.has(file.id) ? { ...file, folderId } : file));
      });
      return next;
    });
  };

  // Move the selected files into a folder (or root) — persists via the backend.
  // Optimistic update + revert, mirroring rename / category change.
  const moveFilesToFolder = async (ids: string[], targetFolderId: string | null) => {
    if (ids.length === 0) return;
    const idSet = new Set(ids);
    const snapshot = filesList; // for full revert if the request fails
    applyFolderToState(idSet, targetFolderId);
    if (targetFolderId) setExpandedFolderIds(prev => new Set(prev).add(targetFolderId));
    try {
      // Single-file drag uses the same bulk endpoint with one id.
      const updated = await fileService.moveFiles(ids, targetFolderId);
      // Reconcile with the server response when it returns the canonical files.
      if (Array.isArray(updated) && updated.length > 0) {
        setFilesList(prev => {
          const f = prev as Record<FileCategory, ProjectFile[]>;
          const byId = new Map<string, ProjectFile>(updated.map((u: ProjectFile) => [u.id, u]));
          const next: Record<string, ProjectFile[]> = {};
          (Object.keys(f) as FileCategory[]).forEach(cat => {
            next[cat] = (f[cat] || []).map(file =>
              byId.has(file.id) ? { ...file, folderId: byId.get(file.id)!.folderId ?? null } : file,
            );
          });
          return next;
        });
      }
      exitSelection();
      toast({
        title: ids.length > 1 ? `${ids.length} קבצים הועברו` : 'הקובץ הועבר',
        style: { direction: 'rtl' },
      });
    } catch (error) {
      setFilesList(snapshot); // revert the whole batch
      toast({
        title: 'שגיאה בהעברת הקבצים',
        description: 'אירעה שגיאה בהעברת הקבצים לתיקייה',
        variant: 'destructive',
        style: { direction: 'rtl' },
      });
    }
  };

  // New folder is created under the currently selected folder (root when none) — via the backend.
  const handleCreateFolder = async (name: string) => {
    if (isCreatingFolder) return;
    const parentId = selectedFolderId;
    setIsCreatingFolder(true);
    try {
      const created: Folder = await fileService.createFolder(projectId, {
        name,
        category: activeTab,
        parentId: parentId ?? null,
      });
      setFolders(prev => [...prev, created]);
      if (parentId) setExpandedFolderIds(prev => new Set(prev).add(parentId));
      setSelectedFolderId(created.id);
      setNewFolderOpen(false);
      toast({ title: 'התיקייה נוצרה', style: { direction: 'rtl' } });
    } catch (error) {
      toast({
        title: 'שגיאה ביצירת התיקייה',
        description: 'אירעה שגיאה ביצירת התיקייה',
        variant: 'destructive',
        style: { direction: 'rtl' },
      });
    } finally {
      setIsCreatingFolder(false);
    }
  };

  const selectedFolderName = selectedFolderId
    ? folders.find(f => f.id === selectedFolderId)?.name
    : undefined;

  useEffect(() => () => {
    if (longPressTimerRef.current) clearTimeout(longPressTimerRef.current);
  }, []);

  const exitSelection = () => {
    setSelectionMode(false);
    setSelectedIds(new Set());
  };

  const toggleSelection = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      if (next.size === 0) setSelectionMode(false);
      return next;
    });
  };

  const clearLongPressTimer = () => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  };

  // Web: plain onClick preview. Android: long-press enters selection mode, then taps toggle.
  const tileHandlers = (id: string, onTap?: () => void) => {
    if (!isAndroid) {
      return { onClick: onTap };
    }
    return {
      onTouchStart: () => {
        longPressFiredRef.current = false;
        clearLongPressTimer();
        longPressTimerRef.current = setTimeout(() => {
          longPressFiredRef.current = true;
          setSelectionMode(true);
          setSelectedIds(prev => new Set(prev).add(id));
        }, 500);
      },
      onTouchEnd: clearLongPressTimer,
      onTouchMove: clearLongPressTimer,
      onTouchCancel: clearLongPressTimer,
      onContextMenu: (e: React.MouseEvent) => e.preventDefault(),
      onClick: () => {
        if (longPressFiredRef.current) {
          // Consume the click that follows a long-press so it doesn't open the file.
          longPressFiredRef.current = false;
          return;
        }
        if (selectionMode) {
          toggleSelection(id);
          return;
        }
        onTap?.();
      },
    };
  };

  const selectionToolbarVisible = (isAndroid && selectionMode) || (!isAndroid && selectedIds.size > 0);

  useEffect(() => {
    const fetchFiles = async () => {
      try {
        const fileList = await fileService.getFilesByProjectId(projectId);

        let filesData = {
          plans: [],
          payments: [],
          contracts: [],
          photos: []
        };
        filesData.plans = fileList.filter((file: ProjectFile) => file.category === 'plans');
        filesData.payments = fileList.filter((file: ProjectFile) => file.category === 'payments');
        filesData.contracts = fileList.filter((file: ProjectFile) => file.category === 'contracts');
        filesData.photos = fileList.filter((file: ProjectFile) => file.category === 'photos');
        setFilesList(filesData);
      } catch (error) {
        console.error('Error fetching files:', error);
      }
    };

    // Folders load independently so a folder failure never breaks the (flat) files view.
    const fetchFolders = async () => {
      setFoldersLoading(true);
      try {
        const folderList = await fileService.getFolders(projectId);
        setFolders(Array.isArray(folderList) ? folderList : []);
      } catch (error) {
        console.error('Error fetching folders:', error);
        setFolders([]);
        toast({
          title: 'שגיאה בטעינת התיקיות',
          description: 'לא ניתן היה לטעון את התיקיות. תצוגה שטוחה עדיין זמינה.',
          variant: 'destructive',
          style: { direction: 'rtl' },
        });
      } finally {
        setFoldersLoading(false);
      }
    };

    fetchFiles();
    fetchFolders();
  }, [projectId]);

  useEffect(() => {
    setPdfCurrentPage(1);
    setPdfNumPages(0);
  }, [selectedFile]);

  // Get all photos for this project
  const allPhotos = useMemo(() => {
    return filesList["photos"] || [];
  }, [projectId, filesList]);

  // Extract unique available tags from photos
  const availableTags = useMemo((): any[] => {
    let allTags = Array.from(new Set(allPhotos.flatMap(p => p.event.tags || [])));
    return Array.from(new Set(allTags.flatMap((tag: any) => tag.name || [])))
  }, [allPhotos]);

  // Filter photos based on selected tags (OR logic)
  const filteredPhotos = useMemo(() => {

    if (selectedTags.length === 0) return allPhotos;
    return allPhotos.filter(photo => {
      // בודק אם קיים לפחות תג אחד שהשם שלו נמצא במערך התגים הנבחרים
      return photo.event?.tags?.some(tag => selectedTags.includes(tag.name));
    });
    return allPhotos.filter(p => selectedTags.some(t => (p.event.tags || []).find(t => t.name)))
  }, [allPhotos, selectedTags]);

  // Detection delegates to the shared helper; same name + call sites, same three signals (application/pdf | pdf | .pdf name).
  const isPdfFile = (file: ProjectFile): boolean =>
    isPdfMedia({ type: file.type, name: file.name });

  const isPreviewable = (file: ProjectFile): boolean => {
    return isImageMedia({ type: file.type }) || isVideoMedia({ type: file.type }) || isPdfFile(file);
  };

  const handleFileClick = async (file: ProjectFile) => {
    if (isImageMedia({ type: file.type })) {
      const photoFiles = filteredPhotos.filter(f => isImageMedia({ type: f.type }));
      const index = photoFiles.findIndex(f => f.id === file.id);
      setGalleryIndex(index);
      setIsGalleryOpen(true);
    } else if (isPdfFile(file)) {
      // Android: open in the device's native PDF viewer; fall back to the in-app modal on failure.
      if (canOpenPdfNatively(file.url)) {
        const res = await openPdfNative({ url: file.url, fileName: file.name });
        if (res.opened) return;
        if (res.reason === 'download-failed') {
          tst.error('פתיחת הקובץ נכשלה', { style: { direction: 'rtl' } });
        }
      }
      setSelectedFile(file);
    } else if (isVideoMedia({ type: file.type })) {
      setSelectedFile(file);
    } else {
      triggerDownload(file);
    }
  };

  const triggerDownload = async (file: ProjectFile) => {
    if (file.url === '#mock-url') {
      // ... logic for mock url (toast)
      return;
    }

    const result = await downloadMedia({
      url: file.url,
      fileName: file.name || 'download.mp4',
      mimeType: file.type === "VIDEO" ? 'video/mp4' : file.type || 'application/octet-stream',
    });

    if (!result.success) {
      tst.error('ההורדה נכשלה', { style: { direction: 'rtl' } });
      return;
    }

    // Native saves to the public Downloads folder and confirms it; the web path
    // (no toast before this migration) keeps its no-success-toast behavior.
    if (result.platform === 'native') {
      tst.success('הקובץ נשמר בתיקיית ההורדות', { style: { direction: 'rtl' } });
    }
  };

  // Flat view of every file across categories — used to resolve selected files by id.
  const allFiles = useMemo<ProjectFile[]>(() => {
    const f = filesList as Record<FileCategory, ProjectFile[]>;
    return [...(f.plans || []), ...(f.payments || []), ...(f.contracts || []), ...(f.photos || [])];
  }, [filesList]);

  const getSelectedFileName = (id: string | null): string =>
    (id && allFiles.find(f => f.id === id)?.name) || '';

  // Applies a name to the matching file across every category array (immutable).
  const applyRenameToState = (id: string, name: string) => {
    setFilesList(prev => {
      const f = prev as Record<FileCategory, ProjectFile[]>;
      const next: Record<string, ProjectFile[]> = {};
      (Object.keys(f) as FileCategory[]).forEach(cat => {
        next[cat] = (f[cat] || []).map(file => (file.id === id ? { ...file, name } : file));
      });
      return next;
    });
  };

  const handleShare = async () => {
    if (selectedIds.size === 0) return;
    setIsSharing(true);
    try {
      const items: ShareItem[] = [];
      selectedIds.forEach(id => {
        const file = allFiles.find(f => f.id === id);
        const url = file?.url || file?.thumbnailUrl;
        // Skip files without a usable, real URL (e.g. mock placeholders).
        if (file && url && url !== '#mock-url') {
          items.push({ kind: 'remote', fileName: file.name, url });
        }
      });
      if (items.length === 0) {
        toast({ title: 'לא ניתן לשתף את הקבצים שנבחרו', variant: 'destructive' });
        return;
      }
      if (Capacitor.isNativePlatform()) {
        await shareAttachments(items);
      } else {
        await shareAttachmentsWeb(items);
      }
      exitSelection();
    } catch (error) {
      toast({
        title: 'שגיאה בשיתוף',
        description: 'אירעה שגיאה בשיתוף הקבצים',
        variant: 'destructive',
      });
    } finally {
      setIsSharing(false);
    }
  };

  const handleRename = async (newName: string) => {
    const id = renameTargetId;
    if (!id || isRenaming) return;
    setIsRenaming(true);
    const prevName = allFiles.find(f => f.id === id)?.name;
    // Optimistic update.
    applyRenameToState(id, newName);
    try {
      const updated = await fileService.renameFile(id, newName);
      // Reconcile with the server response when it returns the canonical name.
      if (updated && typeof updated.name === 'string') {
        applyRenameToState(id, updated.name);
      }
      toast({ title: 'שם הקובץ עודכן', style: { direction: 'rtl' } });
      setRenameTargetId(null);
      exitSelection();
    } catch (error) {
      // Revert on failure.
      if (prevName !== undefined) applyRenameToState(id, prevName);
      toast({
        title: 'שגיאה בשינוי שם הקובץ',
        description: 'אירעה שגיאה בעדכון שם הקובץ',
        variant: 'destructive',
        style: { direction: 'rtl' },
      });
    } finally {
      setIsRenaming(false);
    }
  };

  // Only document categories are valid move targets — never photos/videos.
  const docCategoryOptions: { value: FileCategory; label: string }[] =
    (['plans', 'payments', 'contracts'] as FileCategory[]).map(c => ({ value: c, label: categoryLabels[c] }));

  const handleChangeCategory = async (newCategory: FileCategory) => {
    if (isChangingCategory) return;
    // Defensive: never touch photos, and skip files already in the target category.
    const targetFiles = allFiles.filter(
      f => selectedIds.has(f.id) && f.category !== 'photos' && f.category !== newCategory
    );
    if (targetFiles.length === 0) {
      setCategoryDialogOpen(false);
      exitSelection();
      return;
    }
    const targetIds = new Set(targetFiles.map(f => f.id));
    const snapshot = filesList; // for full revert if any PATCH fails
    setIsChangingCategory(true);

    // Optimistically move every target file into the new category array.
    // A folder is category-scoped, so a category change detaches the file → folderId null
    // (mirrors the backend, which resets folderId when the category no longer matches).
    setFilesList(prev => {
      const f = prev as Record<FileCategory, ProjectFile[]>;
      const moved: ProjectFile[] = [];
      const next: Record<string, ProjectFile[]> = {};
      (Object.keys(f) as FileCategory[]).forEach(cat => {
        next[cat] = (f[cat] || []).filter(file => {
          if (targetIds.has(file.id)) {
            moved.push({ ...file, category: newCategory, folderId: null });
            return false;
          }
          return true;
        });
      });
      next[newCategory] = [...(next[newCategory] || []), ...moved];
      return next;
    });

    try {
      // If any file fails, Promise.all rejects and we revert the whole batch.
      const updatedFiles = await Promise.all(
        targetFiles.map(f => fileService.updateFileCategory(f.id, newCategory)),
      );
      // Reconcile folderId with the server response when it returns the canonical files.
      const byId = new Map<string, ProjectFile>(
        (updatedFiles || [])
          .filter((u): u is ProjectFile => !!u && typeof u.id === 'string')
          .map(u => [u.id, u]),
      );
      if (byId.size > 0) {
        setFilesList(prev => {
          const f = prev as Record<FileCategory, ProjectFile[]>;
          const next: Record<string, ProjectFile[]> = {};
          (Object.keys(f) as FileCategory[]).forEach(cat => {
            next[cat] = (f[cat] || []).map(file =>
              byId.has(file.id) ? { ...file, folderId: byId.get(file.id)!.folderId ?? null } : file,
            );
          });
          return next;
        });
      }
      toast({ title: 'הסיווג עודכן', style: { direction: 'rtl' } });
      setCategoryDialogOpen(false);
      exitSelection();
    } catch (error) {
      setFilesList(snapshot); // revert all moved files to the original state
      toast({
        title: 'שגיאה בשינוי הסיווג',
        description: 'אירעה שגיאה בעדכון סיווג הקבצים',
        variant: 'destructive',
        style: { direction: 'rtl' },
      });
    } finally {
      setIsChangingCategory(false);
    }
  };

  const handleFileUpload = async (category: FileCategory, files: FileList | null) => {
    if (!files || files.length === 0) return;

    const file = files[0];

    try {
      const formData = new FormData();
      formData.append('file', file, file.name);
      formData.append('projectId', projectId);
      formData.append('category', activeTab);

      fileService.UploadFile(formData).then((data) => {
        filesList[category] = [...(filesList[category] || []), data];
        setFilesList({ ...filesList });
        toast({
          title: 'הקובץ הועלה בהצלחה',
          description: `הקובץ "${file.name}" הועלה לקטגוריה ${categoryLabels[category]}`,
        });
      }).catch((error) => {

      });
    } catch (error) {
      toast({
        title: 'שגיאה בהעלאת הקובץ',
        description: error instanceof Error ? error.message : 'אירעה שגיאה לא צפויה',
        variant: 'destructive'
      });
    }

    const inputRef = fileInputRefs.current[category];
    if (inputRef) inputRef.value = '';
  };


  const toggleTag = (tag: string) => {

    setSelectedTags(prev =>
      prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]
    );
  };

  const clearTags = () => {
    setSelectedTags([]);
  };

  const renderTagFilter = () => {

    if (availableTags.length === 0) return null;

    return (
      <div className="mb-4">
        <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-hide">
          {availableTags.map((tag) => (
            <button
              key={tag}
              onClick={() => toggleTag(tag)}
              className={cn(
                "px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-all flex-shrink-0",
                selectedTags.includes(tag)
                  ? "bg-primary text-primary-foreground"
                  : "bg-secondary text-secondary-foreground border border-border hover:border-primary"
              )}
            >
              {tag}
            </button>
          ))}
          {selectedTags.length > 0 && (
            <button
              onClick={clearTags}
              className="px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap bg-destructive/10 text-destructive hover:bg-destructive/20 transition-colors flex-shrink-0"
            >
              נקה
            </button>
          )}
        </div>
      </div>
    );
  };

  const renderPhotosSection = () => {
    if (filteredPhotos.length === 0) {
      if (selectedTags.length > 0) {
        // Filtered but no results
        return (
          <div className="text-center py-12 text-muted-foreground">
            <ImageIcon className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p className="mb-4">לא נמצאו תמונות התואמות את הסינון</p>
            <Button variant="outline" onClick={clearTags}>
              נקה סינון
            </Button>
          </div>
        );
      }
      // No photos at all
      return (
        <div className="text-center py-12 text-muted-foreground">
          <ImageIcon className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p>אין מסמכים בקטגוריה זו</p>
        </div>
      );
    }

    return (
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        {filteredPhotos.map((file) => {
          const isSelected = selectedIds.has(file.id);
          return (
          <div
            key={file.id}
            {...tileHandlers(file.id, () => handleFileClick(file))}
            className={cn(
              "aspect-square bg-secondary rounded-xl overflow-hidden relative group cursor-pointer hover:ring-2 hover:ring-primary transition-all",
              isAndroid && "select-none",
              isSelected && "ring-2 ring-primary"
            )}
          >
            {isVideoMedia({ type: file.type }) ? (
              <div className="w-full h-full relative">
                <img
                  src={file.thumbnailUrl}
                  alt={file.name}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-12 h-12 bg-black/50 rounded-full flex items-center justify-center">
                    <Video className="w-6 h-6 text-white" />
                  </div>
                </div>
              </div>
            ) : (
              <img
                src={file.thumbnailUrl}
                alt={file.name}
                className="w-full h-full object-cover"
              />
            )}
            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
              <p className="text-white text-xs text-center px-2 truncate">{file.name}</p>
            </div>
            {/* Tag indicators on hover */}
            {(file.tags || []).length > 0 && (
              <div className="absolute bottom-1 right-1 left-1 flex flex-wrap gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                {(file.tags || []).slice(0, 2).map((tag, idx) => (
                  <span key={idx} className="bg-primary/80 text-primary-foreground text-[10px] px-1.5 py-0.5 rounded">
                    {tag}
                  </span>
                ))}
                {(file.tags || []).length > 2 && (
                  <span className="bg-primary/80 text-primary-foreground text-[10px] px-1.5 py-0.5 rounded">
                    +{(file.tags || []).length - 2}
                  </span>
                )}
              </div>
            )}

            {/* Selected overlay */}
            {isSelected && <div className="absolute inset-0 bg-primary/20 pointer-events-none" />}

            {isAndroid && selectionMode ? (
              /* Android: tap toggles selection, so this indicator is non-interactive. */
              <div className="absolute top-1 right-1 pointer-events-none">
                {isSelected ? (
                  <CheckCircle2 className="w-6 h-6 text-primary fill-primary-foreground" />
                ) : (
                  <Circle className="w-6 h-6 text-white/90 drop-shadow" />
                )}
              </div>
            ) : (
              !isAndroid && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleSelection(file.id);
                  }}
                  className={cn(
                    "absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity",
                    isSelected && "opacity-100"
                  )}
                  aria-label={isSelected ? 'בטל בחירה' : 'בחר'}
                >
                  {isSelected ? (
                    <CheckCircle2 className="w-6 h-6 text-primary fill-primary-foreground" />
                  ) : (
                    <Circle className="w-6 h-6 text-white/90 drop-shadow" />
                  )}
                </button>
              )
            )}
          </div>
          );
        })}
      </div>
    );
  };

  const renderFileList = (category: FileCategory) => {
    const files = filesList[category] || [];

    if (files.length === 0) {
      return (
        <div className="text-center py-12 text-muted-foreground">
          <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p>אין מסמכים בקטגוריה זו</p>
        </div>
      );
    }

    return (
      <div className="space-y-2">
        {files.map((file) => {
          const isSelected = selectedIds.has(file.id);
          return (
          <div
            key={file.id}
            {...tileHandlers(file.id, () => handleFileClick(file))}
            className={cn(
              "bg-card rounded-xl p-4 border border-border flex items-center gap-3 cursor-pointer hover:bg-secondary/50 transition-colors group",
              isAndroid && "select-none",
              isSelected && "ring-2 ring-primary bg-primary/5"
            )}
          >
            <div className="w-10 h-10 rounded-lg bg-secondary flex items-center justify-center flex-shrink-0">
              <FileText className="w-5 h-5 text-muted-foreground" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-medium truncate">{file.name}</p>
              <p className="text-xs text-muted-foreground">
                {format(new Date(file.createdAt), 'd בMMMM yyyy', { locale: he })}
              </p>
            </div>
            {isAndroid && selectionMode ? (
              /* Android: tapping the row toggles selection, so this indicator is non-interactive. */
              <div className="flex-shrink-0 pointer-events-none">
                {isSelected ? (
                  <CheckCircle2 className="w-6 h-6 text-primary" />
                ) : (
                  <Circle className="w-6 h-6 text-muted-foreground/60" />
                )}
              </div>
            ) : (
              <>
                {!isAndroid && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleSelection(file.id);
                    }}
                    className={cn(
                      "flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity",
                      isSelected && "opacity-100"
                    )}
                    aria-label={isSelected ? 'בטל בחירה' : 'בחר'}
                  >
                    {isSelected ? (
                      <CheckCircle2 className="w-6 h-6 text-primary" />
                    ) : (
                      <Circle className="w-6 h-6 text-muted-foreground/60" />
                    )}
                  </button>
                )}
                <Button
                  variant="ghost"
                  size="icon"
                  className="flex-shrink-0"
                  onClick={(e) => {
                    e.stopPropagation();
                    triggerDownload(file);
                  }}
                >
                  <Download className="w-4 h-4" />
                </Button>
              </>
            )}
          </div>
          );
        })}
      </div>
    );
  };

  const renderUploadButton = (category: FileCategory) => {
    const isPhotos = category === 'photos';
    const accept = isPhotos ? 'image/*,video/*' : '.pdf,.doc,.docx,.xls,.xlsx';

    return (
      <div className="mb-4">
        <Input
          ref={(el) => { fileInputRefs.current[category] = el; }}
          type="file"
          accept={accept}
          className="hidden"
          onChange={(e) => handleFileUpload(category, e.target.files)}
        />
        <Button
          variant="outline"
          className="w-full"
          onClick={() => fileInputRefs.current[category]?.click()}
        >
          <Upload className="w-4 h-4 ml-2" />
          העלה {isPhotos ? 'תמונה/וידאו' : 'קובץ'}
        </Button>
      </div>
    );
  };

  const handleTabChange = (value: string) => {
    // Selection is scoped to the visible category — reset it when switching tabs.
    exitSelection();
    // Folder selection is per-category too — clear it so a new folder isn't parented across tabs.
    setSelectedFolderId(null);
    setActiveTab(value as FileCategory);
  }

  const renderPreviewContent = () => {
    if (!selectedFile) return null;

    if (isVideoMedia({ type: selectedFile.type })) {
      return (
        <video
          src={selectedFile.url}
          controls
          className="w-full h-[80vh] rounded-lg"
          style={{ maxHeight: '80vh' }}
        >
          הדפדפן שלך לא תומך בהצגת וידאו.
        </video>
      );
    }

    if (isPdfFile(selectedFile)) {
      if (selectedFile.url === '#mock-url') {
        return (
          <div className="flex items-center justify-center h-[80vh] text-muted-foreground">
            <div className="text-center">
              <FileText className="w-16 h-16 mx-auto mb-4 opacity-50" />
              <p>תצוגה מקדימה לא זמינה לקובץ לדוגמה</p>
            </div>
          </div>
        );
      }
      return (
        <div className="flex flex-col items-center gap-3">
          <Document
            file={selectedFile.url}
            onLoadSuccess={({ numPages }) => setPdfNumPages(numPages)}
            loading={
              <div className="flex items-center justify-center h-[70vh] text-muted-foreground">
                <div className="text-center">
                  <FileText className="w-12 h-12 mx-auto mb-3 animate-pulse opacity-50" />
                  <p className="text-sm">טוען מסמך...</p>
                </div>
              </div>
            }
            error={
              <div className="flex items-center justify-center h-[70vh] text-destructive">
                <div className="text-center">
                  <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p className="text-sm">שגיאה בטעינת המסמך</p>
                </div>
              </div>
            }
            className="w-full"
          >
            {pdfNumPages > 0 && (
              <Page
                pageNumber={pdfCurrentPage}
                width={Math.min(
                  typeof window !== 'undefined' ? window.innerWidth * 0.85 : 600,
                  760
                )}
                renderTextLayer={false}
                renderAnnotationLayer={false}
              />
            )}
          </Document>

          {pdfNumPages > 1 && (
            <div className="flex items-center gap-4 pb-2" dir="ltr">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPdfCurrentPage(p => Math.max(1, p - 1))}
                disabled={pdfCurrentPage <= 1}
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
              <span className="text-sm text-muted-foreground">
                {pdfCurrentPage} / {pdfNumPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPdfCurrentPage(p => Math.min(pdfNumPages, p + 1))}
                disabled={pdfCurrentPage >= pdfNumPages}
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
            </div>
          )}
        </div>
      );
    }

    return null;
  };

  // Bundle of state + handlers threaded into the folder tree for a given category.
  const buildTreeShared = (category: FileCategory): FolderTreeShared => ({
    category,
    folders,
    files: (filesList as Record<FileCategory, ProjectFile[]>)[category] || [],
    expandedFolderIds,
    selectedFolderId,
    selectedIds,
    isAndroid,
    selectionMode,
    tileHandlers,
    onToggleExpand: toggleFolderExpand,
    onSelectFolder: setSelectedFolderId,
    onFileClick: handleFileClick,
    onToggleSelection: toggleSelection,
    onDownload: triggerDownload,
  });

  const categories: FileCategory[] = ['plans', 'payments', 'contracts', 'photos'];

  return (
    <>
      <Tabs defaultValue='plans' value={activeTab} onValueChange={handleTabChange} className="w-full" dir="rtl">
        <TabsList className="w-full grid grid-cols-4 mb-4">
          {categories.map((cat) => (
            <TabsTrigger key={cat} value={cat} className="text-xs md:text-sm">
              {categoryLabels[cat]}
            </TabsTrigger>
          ))}
        </TabsList>

        {/* View toggle + New Folder — document tabs only (Photos stays a flat grid). */}
        {activeTab !== 'photos' && (
          <div className="flex items-center justify-between gap-2 mb-4">
            <ToggleGroup
              type="single"
              value={viewMode}
              onValueChange={(v) => v && setViewMode(v as 'flat' | 'folder')}
              className="justify-start"
            >
              <ToggleGroupItem value="flat" aria-label="תצוגה שטוחה" className="gap-1.5 text-xs">
                <List className="w-4 h-4" />
                תצוגה שטוחה
              </ToggleGroupItem>
              <ToggleGroupItem value="folder" aria-label="תצוגת תיקיות" className="gap-1.5 text-xs">
                <FolderTreeIcon className="w-4 h-4" />
                תצוגת תיקיות
              </ToggleGroupItem>
            </ToggleGroup>

            {viewMode === 'folder' && (
              <Button variant="outline" size="sm" className="gap-1.5" onClick={() => setNewFolderOpen(true)}>
                <FolderPlus className="w-4 h-4" />
                תיקייה חדשה
              </Button>
            )}
          </div>
        )}

        {/* Selection toolbar — Share (multi) + Rename (single) */}
        {selectionToolbarVisible && (
          <div className="flex items-center justify-between gap-2 mb-4 px-3 py-2 rounded-xl bg-primary/10 border border-primary/30">
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={exitSelection}
                className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-secondary transition-colors"
                aria-label="בטל בחירה"
              >
                <X className="w-4 h-4" />
              </button>
              <span className="text-sm font-medium">{selectedIds.size} נבחרו</span>
            </div>
            <div className="flex items-center gap-2">
              {/* Category change applies to document files only — hidden on the photos/media tab. */}
              {activeTab !== 'photos' && (
                <button
                  type="button"
                  onClick={() => setCategoryDialogOpen(true)}
                  disabled={isSharing}
                  className="flex items-center gap-1.5 px-4 py-1.5 rounded-lg bg-secondary text-secondary-foreground text-sm font-medium disabled:opacity-50 hover:opacity-90 transition-opacity"
                >
                  <FolderInput className="w-4 h-4" />
                  שנה סיווג
                </button>
              )}
              {selectedIds.size === 1 && (
                <button
                  type="button"
                  onClick={() => setRenameTargetId([...selectedIds][0])}
                  disabled={isSharing}
                  className="flex items-center gap-1.5 px-4 py-1.5 rounded-lg bg-secondary text-secondary-foreground text-sm font-medium disabled:opacity-50 hover:opacity-90 transition-opacity"
                >
                  <Pencil className="w-4 h-4" />
                  שנה שם
                </button>
              )}
              <button
                type="button"
                onClick={handleShare}
                disabled={isSharing || selectedIds.size === 0}
                className="flex items-center gap-1.5 px-4 py-1.5 rounded-lg bg-primary text-primary-foreground text-sm font-medium disabled:opacity-50 hover:opacity-90 transition-opacity"
              >
                {isSharing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Share2 className="w-4 h-4" />}
                שתף
              </button>
            </div>
          </div>
        )}

        {categories.map((cat) => (
          <TabsContent key={cat} value={cat}>
            {renderUploadButton(cat)}
            {cat === 'photos' ? (
              <>
                {renderTagFilter()}
                {renderPhotosSection()}
              </>
            ) : viewMode === 'folder' ? (
              <FolderTree
                shared={buildTreeShared(cat)}
                onMoveFiles={moveFilesToFolder}
                loading={foldersLoading}
                onNewFolder={() => setNewFolderOpen(true)}
              />
            ) : (
              renderFileList(cat)
            )}
          </TabsContent>
        ))}
      </Tabs>

      {/* Preview Modal for PDFs and Videos */}
      <Dialog open={!!selectedFile && !isImageMedia({ type: selectedFile.type })} onOpenChange={(open) => !open && setSelectedFile(null)}>
        <DialogContent className="max-w-4xl w-[95vw] max-h-[90vh] flex flex-col" dir="rtl">
          <DialogHeader className="flex flex-row items-center justify-between">
            <DialogTitle className="truncate flex-1 text-right">
              {selectedFile?.name}
            </DialogTitle>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => selectedFile && triggerDownload(selectedFile)}
              >
                <Download className="w-4 h-4 ml-2" />
                הורד
              </Button>
            </div>
          </DialogHeader>
        <div className="overflow-y-auto flex-1">
          {renderPreviewContent()}
        </div>
        </DialogContent>
      </Dialog>

      {/* Image Gallery */}
      {isGalleryOpen && (
        <ImageGallery
          images={filteredPhotos.filter(f => isImageMedia({ type: f.type }))}
          initialIndex={galleryIndex}
          isOpen={isGalleryOpen}
          onClose={() => setIsGalleryOpen(false)}
          enableDownload={true}
        />
      )}

      <RenameFileDialog
        open={renameTargetId !== null}
        currentName={getSelectedFileName(renameTargetId)}
        isSaving={isRenaming}
        onCancel={() => setRenameTargetId(null)}
        onSave={handleRename}
      />

      <ChangeCategoryDialog
        open={categoryDialogOpen}
        count={selectedIds.size}
        currentCategory={activeTab}
        options={docCategoryOptions}
        isSaving={isChangingCategory}
        onCancel={() => setCategoryDialogOpen(false)}
        onSave={handleChangeCategory}
      />

      <NewFolderDialog
        open={newFolderOpen}
        parentName={selectedFolderName}
        isSaving={isCreatingFolder}
        onCancel={() => setNewFolderOpen(false)}
        onSave={handleCreateFolder}
      />
    </>
  );
}